import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'tertiary' | 'login' | 'link';

export type ButtonSize = 'sm';

interface ButtonShared {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  onClick?: () => void;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  ariaLabel?: string;
  dataTestId?: string;
}

type ButtonElementProps = ButtonShared &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'onClick' | 'children'> & {
    to?: never;
    href?: never;
  };

type ButtonLinkProps = ButtonShared &
  Omit<LinkProps, 'className' | 'onClick' | 'children' | 'to'> & {
    to: string;
    href?: never;
  };

type ButtonAnchorProps = ButtonShared &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'onClick' | 'children' | 'href'> & {
    href: string;
    to?: never;
  };

export type ButtonProps = ButtonElementProps | ButtonLinkProps | ButtonAnchorProps;

export function Button(props: ButtonProps) {
  const {
    children,
    variant = 'primary',
    size,
    className = '',
    onClick,
    startIcon,
    endIcon,
    ariaLabel,
    dataTestId,
    ...rest
  } = props;
  const classes = ['button', `button--${variant}`, size ? `button--${size}` : '', className].filter(Boolean).join(' ');
  const content = (
    <>
      {startIcon ? <span className="button__icon">{startIcon}</span> : null}
      {children}
      {endIcon ? <span className="button__icon">{endIcon}</span> : null}
    </>
  );
  const shared = {
    className: classes,
    ...(ariaLabel !== undefined && { 'aria-label': ariaLabel }),
    ...(dataTestId !== undefined && { 'data-testid': dataTestId }),
    onClick,
  };

  if ('to' in props && props.to) {
    const { to, ...linkRest } = rest as Omit<ButtonLinkProps, keyof ButtonShared>;
    return (
      <Link to={to} {...linkRest} {...shared}>
        {content}
      </Link>
    );
  }

  if ('href' in props && props.href) {
    const { href, ...anchorRest } = rest as Omit<ButtonAnchorProps, keyof ButtonShared>;
    return (
      <a
        href={href}
        {...anchorRest}
        {...shared}
        rel={props.target === '_blank' ? 'noopener noreferrer' : anchorRest.rel}
      >
        {content}
      </a>
    );
  }

  const { type, ...buttonRest } = rest as Omit<ButtonElementProps, keyof ButtonShared>;
  return (
    <button type={type ?? 'button'} {...buttonRest} {...shared}>
      {content}
    </button>
  );
}
